import Button from "components/atoms/Button/Button";
import Icon from "components/atoms/Icon/Icon";
import InputField from "components/atoms/InputField/InputField";
import InputSelect from "components/atoms/InputSelect/InputSelect";
import LeadScoringSkeleton from "components/atoms/layout/LeadScoringSkeleton/LeadScoringSkeleton";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createClientICP,
  getClientICP,
  resetCreateClientICP,
  resetUpdateClientICP,
  updateClientICP,
} from "store/slices/icp.slice";
import { setCurrentPage } from "store/slices/ui.slice";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { cleanObject } from "utils/object";
import EntryFormatter from "../../../utils/EntryFormatter";
import {
  ClientICPFilterOperatorENUM,
  GROUPED_OPTIONS_COMPANY,
  OPTIONS_INDUSTRY,
  OPTIONS_LOCATION,
  OPTIONS_TARGET_ACCOUNT,
} from "../SearchMain/SearchMainHelper";
import { Card } from "components/atoms/Card";

interface Filter {
  name: string;
  keyword: string;
  values: (string | any)[] | null;
}

const LeadScoringModule: FC = () => {
  const dispatch = useAppDispatch();
  const { data: ICPData, isLoading: ICPIsLoading } = useAppSelector(
    (state) => state.icp
  );
  const { isLoading: ICPCreateIsLoading, isSuccess: isFilterCreated } =
    useAppSelector((state) => state.icp.createClientICP);
  const { isLoading: ICPUpdateIsLoading, isSuccess: isFilterUpdated } =
    useAppSelector((state) => state.icp.updateClientICP);
  const filtersFromDatabase = useAppSelector((state) => state.filters);
  const { data: user } = useAppSelector((state) => state.user);
  const [isDirty, setIsDirty] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!ICPIsLoading) {
      const timeout = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timeout);
    } else {
      setShowContent(false);
    }
  }, [ICPIsLoading]);
  const filters: Filter[] = [
    {
      name: "Person Location",
      keyword: "person_location",
      values: OPTIONS_LOCATION,
    },
    { name: "Linkedin", keyword: "linkedin", values: null },
    { name: "Job Title", keyword: "job_title", values: null },
    {
      name: "Seniority",
      keyword: "seniorities",
      values: filtersFromDatabase.seniorities.map(({ name }) => ({
        value: name,
        label: name,
      })),
    },
    {
      name: "Department",
      keyword: "departments",
      values: filtersFromDatabase.departaments.map(({ name }) => ({
        value: name,
        label: name,
      })),
    },
    {
      name: "Company Headcount",
      keyword: "employee_count",
      values: filtersFromDatabase.headcount.map(({ name }) => ({
        value: name,
        label: name,
      })),
    },
    {
      name: "Company Revenue",
      keyword: "revenue",
      values: filtersFromDatabase.revenue.map(({ name }) => ({
        value: name,
        label: name,
      })),
    },
    { name: "Industry", keyword: "industry", values: OPTIONS_INDUSTRY },
    {
      name: "Company Name",
      keyword: "organization_name",
      values: GROUPED_OPTIONS_COMPANY,
    },
    {
      name: "Company Location",
      keyword: "organization_location",
      values: OPTIONS_LOCATION,
    },
    {
      name: "Target Account",
      keyword: "target_account",
      values: OPTIONS_TARGET_ACCOUNT,
    },
  ];
  const [filterRows, setFilterRows] = useState<
    {
      name: string;
      value: { label: string; value: string | number }[];
      weight: number;
      options: any[] | null | undefined;
    }[]
  >([{ name: "", value: [], weight: 0, options: [] }]);
  const [minimumIcp, setMinimunIcp] = useState(0);
  const [minimumBehavior, setMinimumBehavior] = useState(0);
  const handleFilterChange = (index: number, name: any) => {
    const selectedFilter = filters.find(
      (filter) => filter.name === name?.value
    );
    const updatedRows = [...filterRows];
    updatedRows[index] = {
      name,
      value: [],
      weight: 0,
      options: selectedFilter?.values,
    };
    setFilterRows(updatedRows);
    setIsDirty(true);
  };
  const handleValueChange = (
    index: number,
    value: { label: string; value: string | number }[] | string
  ) => {
    const updatedRows = [...filterRows];
    if (typeof value !== "string" && value?.length > 1) {
      const isValueHasIsKnownOption = value?.some(
        (el) => el.value === ClientICPFilterOperatorENUM.IS_REQUIRED
      );
      const updatedValue = isValueHasIsKnownOption
        ? // Check if "IS_REQUIRED" is first
          value[0].value === ClientICPFilterOperatorENUM.IS_REQUIRED
          ? value.filter(
              (el) => el.value !== ClientICPFilterOperatorENUM.IS_REQUIRED
            ) // Remove "IS_REQUIRED" if first
          : [
              {
                label: "Is Known",
                value: ClientICPFilterOperatorENUM.IS_REQUIRED,
              },
            ] // Set only "IS_REQUIRED" if not first
        : value; // If no "IS_REQUIRED", keep the value as it is
      updatedRows[index].value = updatedValue;
    } else {
      updatedRows[index].value = value as any;
    }
    setFilterRows(updatedRows);
    setIsDirty(true);
  };
  const handleWeightChange = (index: number, weight: number) => {
    const updatedRows = [...filterRows];
    updatedRows[index].weight = weight;
    setFilterRows(updatedRows);
    setIsDirty(true);
  };
  const addFilterRow = () => {
    setFilterRows([
      { name: "", value: [], weight: 0, options: [] },
      ...filterRows,
    ]);
    setIsDirty(true);
  };
  const removeFilterRow = (index: number) => {
    console.log(index);
    const newFilters = filterRows.filter((_, i) => i !== index);
    setFilterRows(newFilters); // Remove the row at the specified index
    setIsDirty(true);
  };
  const save = () => {
    if (minimumBehavior === 0 || minimumIcp === 0) {
      toast.error(
        "Please set a behavior and ICP score before saving any filters.",
        { toastId: "lead-scoring-threshold" }
      );
      return;
    }
    const isInvalidFilter = filterRows.find(
      (filter: any) =>
        !filter?.name?.value ||
        filter?.name?.value.length === 0 ||
        !filter?.value ||
        filter?.value.length === 0 ||
        !filter?.weight ||
        filter?.weight === 0
    );
    if (isInvalidFilter) {
      toast.error("Please fill all the filters before saving.", {
        toastId: "lead-scoring-filters",
      });
      return;
    }
    const dataToSubmit = {
      filters: filterRows?.map(
        (filter: any) =>
          filter?.name?.value && {
            name: filter.name.value,
            value:
              typeof filter?.value !== "string"
                ? filter?.value.map((v: any) => v?.value)
                : filter?.value,
            weight: filter?.weight,
          }
      ),
      thresholdBehavior: minimumBehavior,
      thresholdIcp: minimumIcp,
    };
    if (!ICPData) {
      dispatch(createClientICP(dataToSubmit));
    } else {
      dispatch(updateClientICP({ ...dataToSubmit }));
    }
    sendTrack();
    toast.success("Lead scoring filters saved!", {
      toastId: "lead-scoring-saved",
    });

    setIsDirty(false);
  };
  const sendTrack = useCallback(() => {
    if (minimumIcp === ICPData?.threshold) return;
    const eventEnum = SegmentEventENUM.LEAD_THRESHOLD_SET;
    const leadThresholdPayload = {
      minimumIcp,
      minimumBehavior,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
    };
    const cleanedFilters = cleanObject(leadThresholdPayload);
    window.analytics.track(eventEnum, cleanedFilters);
  }, [minimumIcp]);
  useEffect(() => {
    dispatch(getClientICP());
  }, [dispatch]);
  useEffect(() => {
    if (filterRows.length === 0) addFilterRow();
  }, [filterRows]);
  useEffect(() => {
    if (!ICPData) return;
    const existingFilters = ICPData.icpFilters || [];
    const populatedFilterRows = existingFilters?.map((filter: any) => ({
      name: filter?.name ? { label: filter.name, value: filter.name } : null,
      value:
        typeof filter?.value !== "string"
          ? filter?.value?.map((fv: any) => ({
              label: EntryFormatter.replaceLabelByValue(fv),
              value: fv,
            }))
          : filter?.value,
      weight: filter?.weight,
      options: filters?.find((f) => f?.name === filter?.name)?.values || null,
    }));

    setFilterRows(populatedFilterRows);
    setMinimunIcp(ICPData?.thresholdMinimumIcp);
    setMinimumBehavior(ICPData?.thresholdMinimumBehavior);
  }, [ICPData]);
  useEffect(() => {
    window.analytics.page("Lead Scoring");
    dispatch(setCurrentPage("scoring"));
  }, []);
  useEffect(() => {
    if (isFilterCreated || isFilterUpdated) {
      dispatch(resetCreateClientICP());
      dispatch(resetUpdateClientICP());
    }
  }, [isFilterCreated, isFilterUpdated]);

  return (
    <>
      {ICPIsLoading && (
        <div className="opacity-100 transition-opacity duration-300">
          <LeadScoringSkeleton />;
        </div>
      )}
      {!ICPIsLoading && (
        <div
          className={`
            ${
              showContent ? "opacity-100" : "opacity-0"
            } transition-opacity duration-300`}
        >
          <Card
            title="Filters"
            subtitle="Add filters to qualify leads based on job title, company, and other criteria. Each filter contributes a custom score to the lead."
            icon="filter"
            buttonComponent={
              <div className="flex nustify-end gap-4 px-5">
                <Button
                  size="sm"
                  onClick={addFilterRow}
                  iconName="filter"
                  iconSize={16}
                  type="outline"
                >
                  New Filter
                </Button>
                <div className="flex flex-col max-w-642">
                  <Button
                    size="sm"
                    onClick={save}
                    iconName="save"
                    isProcessing={ICPCreateIsLoading || ICPUpdateIsLoading}
                    isProcessingTitle="Saving Filters..."
                    disabled={!isDirty}
                  >
                    Save
                  </Button>
                  {isDirty && (
                    <span className="text-primary-500 ml-2 mt-2 text-xs whitespace-nowrap ">
                      *Unsaved changes
                    </span>
                  )}
                </div>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="table style-1 min-w-[1100px]">
                <thead>
                  <tr>
                    <th className="w-1/4">
                      <div className="flex gap-2 justify-center items-center">
                        <Icon
                          name="filter"
                          size={20}
                          color="var(--color-white-500)"
                        />
                        <div className="text-white-500 font-bold">Filter</div>
                      </div>
                    </th>
                    <th className="w-1/4">
                      <div className="flex justify-center items-center gap-2">
                        <Icon
                          name="document"
                          size={20}
                          color="var(--color-white-500)"
                        />
                        <div className="text-white-500 font-bold">Value</div>
                      </div>
                    </th>
                    <th>
                      <div className="flex justify-center items-center gap-2">
                        <Icon
                          name="score"
                          size={20}
                          color="var(--color-white-500)"
                        />
                        <div className="text-white-500 font-bold">Score</div>
                      </div>
                    </th>
                    <th className="w-1/5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filterRows.map((filterRow: any, index) => (
                    <Fragment key={index}>
                      <tr>
                        <td className="">
                          <InputSelect
                            options={
                              filters.map((filter) => ({
                                label: filter.name,
                                value: filter.name,
                              })) as []
                            }
                            state={filterRow.name as any}
                            variant="light"
                            isSearchable={false}
                            setState={(name: string) =>
                              handleFilterChange(index, name)
                            }
                            placeholder="Select Filter"
                            isMulti={false}
                          />
                        </td>
                        <td>
                          {(filterRows[index]?.options && (
                            <InputSelect
                              options={(filterRows[index]?.options as []) || []}
                              state={filterRows[index]?.value as any}
                              setState={(value: any) =>
                                handleValueChange(index, value)
                              }
                              placeholder="Select Value"
                              isMulti
                              variant="light"
                              isCopyPaste={
                                filterRow.name.label === "Target Account"
                              }
                            />
                          )) || (
                            <InputField
                              size="small"
                              value={filterRows[index]?.value ?? ""}
                              onChange={(e: any) =>
                                handleValueChange(index, e.target.value)
                              }
                            />
                          )}
                        </td>
                        <td>
                          <div className="flex justify-center gap-1 min-w-[365px] w-fulloverflow-x-auto">
                            {Array.from(
                              { length: 10 },
                              (_, index) => index + 1
                            ).map((weight: number) => (
                              <Button
                                size="xs"
                                key={weight}
                                onClick={() =>
                                  handleWeightChange(index, weight)
                                }
                                className={`min-w-8 w-8 !h-8 !border !border-solid !rounded-[8px] !font-bold !text-[14px] ${
                                  filterRows[index]?.weight < weight
                                    ? "!bg-paper-400 !border-gray-700 !text-gray-400"
                                    : "!border-primary-500"
                                }`}
                                disabled={
                                  !filterRows[index].name ||
                                  filterRows[index].name.length === 0
                                }
                              >
                                {weight}
                              </Button>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="flex justify-end">
                            <Button
                              size="xs"
                              iconName="trash"
                              iconSize={16}
                              className={`!border !border-solid !font-bold !text-[14px] !text-white-500  !bg-gray-800 !border-gray-700`}
                              onClick={() => {
                                removeFilterRow(index);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                      <tr key={index} className="p-0">
                        <td className="!p-0" colSpan={4}>
                          <hr className="p-0 border-gray-700" />
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};
export default LeadScoringModule;
